import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Save, X, DollarSign, Users, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL.replace(/\/$/, '')}/api`;

const Settings = () => {
  const navigate = useNavigate();
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    weekday_price: '',
    weekend_price: '',
    max_guests: '',
    overflow_rate: '',
    enabled: true
  });

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  const fetchRoomTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings/rooms`);
      setRoomTypes(response.data);
    } catch (error) {
      console.error('Error fetching room types:', error);
      alert('Failed to load room types');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      weekday_price: '',
      weekend_price: '',
      max_guests: '',
      overflow_rate: '',
      enabled: true
    });
    setEditingRoom(null);
    setShowAddForm(false);
  };

  const openEditForm = (room) => {
    setFormData({
      name: room.name,
      slug: room.slug,
      description: room.description,
      weekday_price: room.weekday_price.toString(),
      weekend_price: room.weekend_price.toString(),
      max_guests: room.max_guests ? room.max_guests.toString() : '',
      overflow_rate: room.overflow_rate.toString(),
      enabled: room.enabled
    });
    setEditingRoom(room);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.slug || !formData.weekday_price || !formData.weekend_price) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        weekday_price: parseFloat(formData.weekday_price),
        weekend_price: parseFloat(formData.weekend_price),
        max_guests: formData.max_guests ? parseInt(formData.max_guests) : null,
        overflow_rate: parseFloat(formData.overflow_rate || 0),
        enabled: formData.enabled
      };

      if (editingRoom) {
        // Update existing
        await axios.put(`${API}/settings/rooms/${editingRoom.id}`, payload);
        alert('✅ Room type updated successfully!');
      } else {
        // Create new
        await axios.post(`${API}/settings/rooms`, payload);
        alert('✅ Room type created successfully!');
      }

      await fetchRoomTypes();
      resetForm();
    } catch (error) {
      console.error('Error saving room type:', error);
      alert(error.response?.data?.detail || 'Failed to save room type');
    }
  };

  const handleDelete = async (roomId, roomName) => {
    if (!window.confirm(`Delete "${roomName}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/settings/rooms/${roomId}`);
      alert('✅ Room type deleted');
      await fetchRoomTypes();
    } catch (error) {
      console.error('Error deleting room type:', error);
      alert('Failed to delete room type');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAFA] to-[#F5E6D3]">
      {/* Header */}
      <div className="bg-[#3D2817] text-white py-8">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold">Settings</h1>
            <p className="text-gray-300 mt-2">Manage room types and pricing</p>
          </div>
          <Button
            onClick={() => navigate('/admin')}
            className="bg-white text-[#3D2817] hover:bg-gray-100 px-6 py-2 rounded-none flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Admin
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <Card className="shadow-2xl border-2 border-[#3D2817]/20 mb-8">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold text-[#3D2817]">Room Types</h2>
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-[#2D5016] hover:bg-[#223E10] text-white px-6 py-2 rounded-none flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Room Type
              </Button>
            </div>

            {loading ? (
              <p className="text-center text-gray-500 py-8">Loading room types...</p>
            ) : roomTypes.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No room types configured</p>
            ) : (
              <div className="space-y-4">
                {roomTypes.map((room) => (
                  <Card key={room.id} className="border-2 border-[#3D2817]/20">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
                        <div className="md:col-span-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Home className="w-5 h-5 text-[#3D2817]" />
                            <h3 className="text-xl font-bold text-[#3D2817]">{room.name}</h3>
                            {!room.enabled && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Disabled</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{room.description}</p>
                          <p className="text-xs text-gray-500 mt-1">Slug: {room.slug}</p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600">Weekday</p>
                          <p className="text-xl font-bold text-[#2D5016]">
                            ₹{room.weekday_price.toLocaleString('en-IN')}
                          </p>
                        </div>

                        <div>
                          <p className="text-sm text-gray-600">Weekend</p>
                          <p className="text-xl font-bold text-[#2D5016]">
                            ₹{room.weekend_price.toLocaleString('en-IN')}
                          </p>
                          {room.max_guests && (
                            <p className="text-xs text-gray-500 mt-1">
                              Max: {room.max_guests} guests (+₹{room.overflow_rate}/guest)
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 justify-end">
                          <Button
                            onClick={() => openEditForm(room)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDelete(room.id, room.name)}
                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Form Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            onClick={resetForm}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-[#3D2817] text-white p-6 flex items-center justify-between">
                <h3 className="text-2xl font-bold">
                  {editingRoom ? 'Edit Room Type' : 'Add New Room Type'}
                </h3>
                <button
                  onClick={resetForm}
                  className="hover:bg-white/20 p-2 rounded-full transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8">
                <div className="space-y-6">
                  {/* Name */}
                  <div>
                    <Label htmlFor="name" className="text-[#3D2817] mb-2 flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      Room Name *
                    </Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., Hobbit House"
                      className="border-[#3D2817]/30"
                      required
                    />
                  </div>

                  {/* Slug */}
                  <div>
                    <Label htmlFor="slug" className="text-[#3D2817] mb-2">
                      Slug (URL-friendly key) *
                    </Label>
                    <Input
                      type="text"
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      placeholder="e.g., hobbit_house"
                      className="border-[#3D2817]/30"
                      pattern="[a-z_]+"
                      required
                      disabled={!!editingRoom}
                    />
                    <p className="text-xs text-gray-500 mt-1">Lowercase letters and underscores only (cannot be changed after creation)</p>
                  </div>

                  {/* Description */}
                  <div>
                    <Label htmlFor="description" className="text-[#3D2817] mb-2">
                      Description
                    </Label>
                    <Input
                      type="text"
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Brief description of the room"
                      className="border-[#3D2817]/30"
                    />
                  </div>

                  {/* Pricing Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="weekday_price" className="text-[#3D2817] mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Weekday Price *
                      </Label>
                      <Input
                        type="number"
                        id="weekday_price"
                        name="weekday_price"
                        value={formData.weekday_price}
                        onChange={handleChange}
                        placeholder="3999"
                        className="border-[#3D2817]/30"
                        min="0"
                        step="1"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="weekend_price" className="text-[#3D2817] mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Weekend Price *
                      </Label>
                      <Input
                        type="number"
                        id="weekend_price"
                        name="weekend_price"
                        value={formData.weekend_price}
                        onChange={handleChange}
                        placeholder="4999"
                        className="border-[#3D2817]/30"
                        min="0"
                        step="1"
                        required
                      />
                    </div>
                  </div>

                  {/* Guest Limits */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="max_guests" className="text-[#3D2817] mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Max Guests
                      </Label>
                      <Input
                        type="number"
                        id="max_guests"
                        name="max_guests"
                        value={formData.max_guests}
                        onChange={handleChange}
                        placeholder="Leave empty for unlimited"
                        className="border-[#3D2817]/30"
                        min="1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="overflow_rate" className="text-[#3D2817] mb-2">
                        Overflow Rate (per guest)
                      </Label>
                      <Input
                        type="number"
                        id="overflow_rate"
                        name="overflow_rate"
                        value={formData.overflow_rate}
                        onChange={handleChange}
                        placeholder="0"
                        className="border-[#3D2817]/30"
                        min="0"
                        step="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Extra charge per guest above max</p>
                    </div>
                  </div>

                  {/* Enabled Checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enabled"
                      name="enabled"
                      checked={formData.enabled}
                      onChange={handleChange}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="enabled" className="text-[#3D2817]">
                      Enable this room type (visible on booking page)
                    </Label>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-4 pt-4">
                    <Button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-none"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-[#2D5016] hover:bg-[#223E10] text-white py-3 rounded-none flex items-center justify-center gap-2"
                    >
                      <Save className="w-5 h-5" />
                      {editingRoom ? 'Update Room Type' : 'Create Room Type'}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Settings;
